'use client';

import { useEffect, useRef, useState, type ComponentPropsWithRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Typography, type TypographyProps } from '@/shared-ui/atoms/typography';
import {
    formatCountdownEndLabel,
    formatCountdownUnit,
    getCountdownMilestone,
    useCountdown,
    DEFAULT_COUNTDOWN_LABEL_LOCALE,
    DEFAULT_COUNTDOWN_LABEL_TIME_ZONE,
    type CountdownMilestone,
    type CountdownMilestoneMessages,
    type CountdownParts,
} from './use-countdown';

type CountdownTypographyVariant = NonNullable<TypographyProps['variant']>;

// Root -----------------------------------------------------------------------

export type RootProps = ComponentPropsWithRef<'div'>;

const Root = ({ className, ref, ...props }: RootProps) => (
    <div
        ref={ref}
        data-slot="countdown-timer"
        role="timer"
        className={cn('inline-flex flex-wrap items-center justify-center', className)}
        {...props}
    />
);
Root.displayName = 'CountdownTimer.Root';

// Unit -----------------------------------------------------------------------

export type UnitProps = ComponentPropsWithRef<'span'>;

const Unit = ({ className, ref, ...props }: UnitProps) => (
    <span
        ref={ref}
        data-slot="countdown-timer-unit"
        className={cn('inline-flex items-baseline whitespace-nowrap', className)}
        {...props}
    />
);
Unit.displayName = 'CountdownTimer.Unit';

// Value ----------------------------------------------------------------------

export type ValueProps = Omit<TypographyProps, 'as' | 'children'> & {
    children: ReactNode;
};

const Value = ({ className, variant = 'h5', ref, children, ...props }: ValueProps) => (
    <Typography
        ref={ref}
        data-slot="countdown-timer-value"
        as="span"
        variant={variant}
        className={cn('tabular-nums', className)}
        {...props}>
        {children}
    </Typography>
);
Value.displayName = 'CountdownTimer.Value';

// Label ----------------------------------------------------------------------

export type LabelProps = Omit<TypographyProps, 'as' | 'children'> & {
    children: ReactNode;
};

const Label = ({ className, variant = 'h5', ref, children, ...props }: LabelProps) => (
    <Typography
        ref={ref}
        data-slot="countdown-timer-label"
        as="span"
        variant={variant}
        className={cn('uppercase', className)}
        {...props}>
        {children}
    </Typography>
);
Label.displayName = 'CountdownTimer.Label';

// Separator ------------------------------------------------------------------

export type SeparatorProps = Omit<TypographyProps, 'as' | 'children'>;

const Separator = ({ className, variant = 'h5', ref, ...props }: SeparatorProps) => (
    <Typography
        ref={ref}
        data-slot="countdown-timer-separator"
        as="span"
        variant={variant}
        aria-hidden="true"
        className={cn('px-1', className)}
        {...props}>
        :
    </Typography>
);
Separator.displayName = 'CountdownTimer.Separator';

// Live -----------------------------------------------------------------------

export type CountdownTimerLabels = {
    unitDays: string;
    unitHours: string;
    unitMinutes: string;
    unitSeconds: string;
    /** Template containing `{{datetime}}` for the static timer name. */
    endingAriaLabel: string;
} & CountdownMilestoneMessages;

export type LiveProps = Omit<RootProps, 'children'> & {
    targetDate: Date;
    variant?: CountdownTypographyVariant;
    /**
     * BCP 47 locale for the end-time aria-label. Pass the site/i18n language
     * (stable on SSR and hydrate) — never rely on environment defaults.
     */
    locale?: string;
    /**
     * IANA time zone for the end-time aria-label. Defaults to UTC so MRT SSR
     * and the browser format the same instant.
     */
    timeZone?: string;
    /**
     * Optional fully localized copy. When omitted, strings come from the
     * `countdownTimer` i18n namespace for the active storefront language.
     */
    labels?: Partial<CountdownTimerLabels>;
};

function useCountdownMilestoneAnnouncement(
    parts: CountdownParts,
    formatAnnouncement: (milestone: CountdownMilestone) => string
): string {
    const [announcement, setAnnouncement] = useState('');
    const previousPartsRef = useRef<CountdownParts | null>(null);
    const formatRef = useRef(formatAnnouncement);
    formatRef.current = formatAnnouncement;

    useEffect(() => {
        const previous = previousPartsRef.current;
        previousPartsRef.current = parts;

        // Skip the mount snapshot — the timer already exposes a static end-time name.
        if (!previous) {
            return;
        }

        const milestone = getCountdownMilestone(previous, parts);
        if (milestone) {
            setAnnouncement(formatRef.current(milestone));
        }
    }, [parts]);

    return announcement;
}

const Live = ({
    targetDate,
    variant = 'h5',
    locale = DEFAULT_COUNTDOWN_LABEL_LOCALE,
    timeZone = DEFAULT_COUNTDOWN_LABEL_TIME_ZONE,
    labels,
    className,
    ...props
}: LiveProps) => {
    const { t } = useTranslation('countdownTimer');
    const parts = useCountdown(targetDate);
    const { days, hours, minutes, seconds } = parts;

    const unitDays = labels?.unitDays ?? t('unitDays');
    const unitHours = labels?.unitHours ?? t('unitHours');
    const unitMinutes = labels?.unitMinutes ?? t('unitMinutes');
    const unitSeconds = labels?.unitSeconds ?? t('unitSeconds');
    const endingAriaLabel = labels?.endingAriaLabel ?? t('endingAriaLabel');

    const formatAnnouncement = (milestone: CountdownMilestone) => {
        switch (milestone.type) {
            case 'ended':
                return labels?.ended ?? t('ended');
            case 'finalMinute':
                return labels?.lessThanOneMinute ?? t('lessThanOneMinute');
            case 'finalHour':
                return labels?.lessThanOneHour ?? t('lessThanOneHour');
            case 'finalDay':
                return labels?.lessThanOneDay ?? t('lessThanOneDay');
            case 'daysRemaining': {
                const override = milestone.days === 1 ? labels?.daysRemainingOne : labels?.daysRemainingOther;

                if (override != null) {
                    return override.replace(/\{\{\s*count\s*\}\}/g, String(milestone.days));
                }

                return t('daysRemaining', { count: milestone.days });
            }
        }
    };

    const milestoneAnnouncement = useCountdownMilestoneAnnouncement(parts, formatAnnouncement);

    return (
        <Root
            className={className}
            aria-label={formatCountdownEndLabel(targetDate, locale, timeZone, endingAriaLabel)}
            {...props}>
            {/* Visual ticks stay non-live so AT is not interrupted every second. */}
            <span aria-hidden="true" className="contents">
                <Unit>
                    <Value variant={variant}>{formatCountdownUnit(days)}</Value>
                    <Label variant={variant}>{unitDays}</Label>
                </Unit>
                <Separator variant={variant} />
                <Unit>
                    <Value variant={variant}>{formatCountdownUnit(hours)}</Value>
                    <Label variant={variant}>{unitHours}</Label>
                </Unit>
                <Separator variant={variant} />
                <Unit>
                    <Value variant={variant}>{formatCountdownUnit(minutes)}</Value>
                    <Label variant={variant}>{unitMinutes}</Label>
                </Unit>
                <Separator variant={variant} />
                <Unit>
                    <Value variant={variant}>{formatCountdownUnit(seconds)}</Value>
                    <Label variant={variant}>{unitSeconds}</Label>
                </Unit>
            </span>
            <span
                data-slot="countdown-timer-announcement"
                className="sr-only"
                role="status"
                aria-live="polite"
                aria-atomic="true">
                {milestoneAnnouncement}
            </span>
        </Root>
    );
};
Live.displayName = 'CountdownTimer.Live';

// Namespace ------------------------------------------------------------------

const CountdownTimer = {
    Root,
    Unit,
    Value,
    Label,
    Separator,
    Live,
} as const;

export { CountdownTimer };
